import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { GoBackLink } from '../GoBackLink'
import { Table } from 'react-bootstrap'
import { Pagination, Switch } from '@CommonComponents/buttons'
import { useStateValue } from '@State'
import { client } from '@Utils'
import { toast } from '@CommonComponents/toast'
import { RemoveConfirmModal } from '../modals/RemoveConfirmModal'
import { EditDeploymentModal } from '../modals/EditDeploymentModal'
import { LoadingSpinner, usePageRequest } from '@CommonComponents'

export const Deployment = ({ match, location, history }) => {
  const [currentState, dispatch] = useStateValue()
  const { text } = window.MEButtons

  const [editInstanceModalOpen, setEditInstanceModalOpen] = useState(false)

  const [removeContextModalOpen, toggleRemoveModal] = useState(false)
  const [removeContextURL, setRemoveContextURL] = useState('')
  const [removeContextMessage, setRemoveContextMessage] = useState('')

  // Pagination — restore page from URL query param on mount (deep link / reload),
  // then keep the URL in sync with the page below so the browser's own back/forward
  // buttons (not just this page's in-app "Go Back" link) land on the right page.
  const initialPage =
    Math.max(1, parseInt(new URLSearchParams(location.search).get('page'))) ||
    1
  const [totalPages, setTotalPages] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [loading, setLoading] = useState(true)
  const {
    page: currentPage,
    attempt: pageAttempt,
    request: changePage,
    settle: settlePage,
  } = usePageRequest(initialPage)

  useEffect(() => {
    history.replace(`${match.url}?page=${currentPage}`)
  }, [currentPage])

  // Filter form
  const defaultFormFilter = {
    context_id: '',
    context_label: '',
    // add other fields here as needed
  }
  const [formData, setFormData] = useState(defaultFormFilter)

  const handleInputChange = event => {
    const { name, value } = event.target
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }))
  }

  useEffect(() => {
    // A later page or filter replaces this load, so its response is dropped.
    let cancelled = false
    const getContexts = async () => {
      setLoading(true)
      try {
        const queryString = new URLSearchParams(formData).toString()
        let resp = await client.get(
          `/account/admin_lti13.getContexts/-/${match.params.issuerId}/${match.params.clientId}/${match.params.deploymentId}/${currentPage}/?${queryString}`
        )
        if (cancelled) return
        dispatch({ type: 'updateMessage', message: null })
        dispatch({ type: 'updateIssuer', issuer: resp.data.issuer })
        dispatch({ type: 'updateClient', client: resp.data.client })
        dispatch({ type: 'updateDeployment', deployment: resp.data.deployment })
        dispatch({
          type: 'updateContextsTable',
          contextsTableData: resp.data.contexts,
        })

        setTotalPages(resp.data.totalPages)
        setPageSize(resp.data.pageSize)
        settlePage()
      } catch (err) {
        if (cancelled) return
        settlePage(err)
        if (err.response) {
          dispatch({ type: 'updateMessage', message: err.response.statusText })
          toast.error(`${err.response.statusText} ${err.response.data.error}`)
       } else {
          dispatch({ type: 'updateMessage', message: err })
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    getContexts()
    return () => {
      cancelled = true
    }
  }, [currentState.reloadSwitch, pageAttempt])

  const pagination =
    totalPages > 1 ? (
      <Pagination
        currentPage={currentPage}
        rate={pageSize}
        total={totalPages * pageSize}
        setCurrentPage={changePage}
      />
    ) : null

  const openModal = contextObject => {
    setRemoveContextURL(
      `/account/admin_lti13.deleteContext/-/${contextObject.id}/`
    )
    setRemoveContextMessage(
      `Are you sure you want to remove ${contextObject.contextTitle}?`
    )
    toggleRemoveModal(true)
  }

  return (
    <>
      <div className="mb-4 go-back-button-left-align">
        <GoBackLink to={`/account/admin_lti13/-/platforms/${match.params.lmsCode}/issuers/${match.params.issuerId}/clients/${match.params.clientId}`} />
      </div>
      <div className="card-myed w-100">
        {currentState.canEdit && (
          <div className="pi-info pull-right">
            <button
              onClick={() => setEditInstanceModalOpen(true)}
              className="btn btn-sm btn-sm-myed btn-primary"
            >
              Edit <i className="fa-regular fa-edit" />
            </button>
          </div>
        )}
        <h3>Deployment:</h3>
        {loading && !currentState.deployment && (
          <LoadingSpinner text="Loading deployment..." outerClass="text-center my-4" />
        )}
        {currentState.issuer &&
          currentState.client &&
          currentState.deployment &&
          currentState.deployment.data && (
            <div>
              {currentState.isPrivileged && (
                <div className="pull-right">
                  <Switch
                    label="Enable LTI 1.3 Connection"
                    data-busy-label={text('strSaving')}
                    data-success-label={text('strChangesSaved')}
                    data-failure-label={text('strSaveFailed')}
                    data-failure-text={text('strTryAgain')}
                    onChange={async () => {
                      let data = {
                        lti_login: !currentState.deployment.data.lti_login,
                      }
                      try {
                        let resp = await client.post(
                          `/account/admin_lti13.lti_login/-/${match.params.issuerId}/${match.params.clientId}/${match.params.deploymentId}`,
                          data
                        )
                        dispatch({
                          type: 'updateDeployment',
                          deployment: resp.data.deployment,
                        })
                        if(resp.data.deployment.data.lti_login) {
                          toast.success('LTI Login Status Updated! LTI login is now: Enabled')
                        } else {
                          toast.warn('LTI Login Status Updated! LTI login is now: Disabled')
                        }
                      } catch (err) {
                        toast.error(`There was an error trying to toggle the Login status: ${err}`)
                        throw err
                      }
                    }}
                    checked={!!currentState.deployment.data.lti_login}
                  />
                  {['blackboard'].includes(match.params.lmsCode) && (
                    <p>Deployments should be enabled for Blackboard</p>
                  )}
                </div>
              )}
              <p>
                <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                  ID:
                </span>{' '}
                {currentState.deployment.id}
              </p>
              <p>
                <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                  Deployment ID:
                </span>{' '}
                {currentState.deployment.deploymentid}
              </p>
              <p>
                <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                  Name:
                </span>{' '}
                {currentState.deployment.data.name}
              </p>
              <p>
                <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                  Connection Enabled:{' '}
                </span>{' '}
                {currentState.deployment.data.lti_login ? 'Yes' : 'No'}
              </p>
              <p>
                <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                  Issuer:{' '}
                </span>{' '}
                {`${currentState.issuer.data.name} (${currentState.issuer.iss}) [${currentState.issuer.id}]`}
              </p>
              <p>
                <span style={{ fontSize: '1.2em', fontWeight: 'bolder' }}>
                  Client:{' '}
                </span>{' '}
                {`${currentState.client.data.name} [${currentState.client.id}]`}
              </p>
            </div>
          )}
      </div>
      <div className="card-myed w-100">
        <h3>Contexts</h3>
        <div style={{ width: '50%', marginBottom: '10px' }}>
          <label htmlFor="context_id">Context ID:</label>
          <input
            id="context_id"
            name="context_id"
            className="form-control"
            placeholder="Context ID"
            value={formData.context_id}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ width: '50%', marginBottom: '10px' }}>
          <label htmlFor="context_label">Context Label:</label>
          <input
            id="context_label"
            name="context_label"
            className="form-control"
            placeholder="Context Label"
            value={formData.context_label}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ marginBottom: '10px' }}>
          <button
            onClick={() => {
              dispatch({ type: 'reload', reload: !currentState.reloadSwitch })
            }}
            className="btn btn-sm btn-sm-myed btn-primary"
          >
            Filter
          </button>
          <button
            onClick={() => {
              setFormData(defaultFormFilter)
              dispatch({ type: 'reload', reload: !currentState.reloadSwitch })
            }}
            className="btn btn-sm btn-sm-myed btn-danger"
          >
            Clear Filters
          </button>
        </div>
        <div className="table-responsive">
        <Table striped bordered className="table-sm" hover>
          <thead>
            <tr>
              <th>#</th>
              <th>Id</th>
              <th>Context Id</th>
              <th>Context Title</th>
              <th style={{ minWidth: 90 }}>Context Label</th>
              <th style={{ minWidth: 90 }}>Create Date</th>
              <th>Links</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="8" className="text-center py-4">
                  <LoadingSpinner text="Loading contexts..." />
                </td>
              </tr>
            )}
            {!loading &&
              (!currentState.contextsTableData ||
                currentState.contextsTableData.length === 0) && (
                <tr>
                  <td colSpan="8" className="text-muted">
                    No contexts found.
                  </td>
                </tr>
              )}
            {!loading &&
              currentState.contextsTableData &&
              currentState.contextsTableData.map((context, i) => {
                const lineItemsUrl = context.lineItems ? (
                  context.lineItems
                ) : (
                  <i
                    className={`far fa-fw mr-4 color_red icon_color_red fa-exclamation-triangle`}
                  />
                )

                const namesRolesUrl = context.namesRoles ? (
                  context.namesRoles
                ) : (
                  <i
                    className={`far fa-fw mr-4 color_red icon_color_red fa-exclamation-triangle`}
                  />
                )

                const redOutlineStyle = { border: '2px solid red' }

                const missingEndpointStyle = context.lineItems
                  ? {}
                  : redOutlineStyle
                return (
                  <tr key={i} style={missingEndpointStyle}>
                    <td>{i + 1 + (currentPage - 1) * pageSize}</td>
                    <td>{context.id}</td>
                    <td>{context.contextId}</td>
                    <td>{context.contextTitle}</td>
                    <td>{context.contextLabel}</td>
                    <td>{new Date(context.createDate).toLocaleDateString()}</td>
                    <td>
                      <p>Line Items: {lineItemsUrl}</p>
                      <p>Names/Roles: {namesRolesUrl}</p>
                    </td>
                    <td>
                      <Link
                        to={`/account/admin_lti13/-/platforms/${match.params.lmsCode}/issuers/${match.params.issuerId}/clients/${match.params.clientId}/deployments/${match.params.deploymentId}/contexts/${context.id}`}
                      >
                        View Context
                      </Link>
                    </td>
                    <td>
                      <a
                        onClick={() => openModal(context)}
                        className="btn btn-sm btn-sm-myed btn-danger"
                      >
                        Remove <i className="fa-regular fa-window-close" />
                      </a>
                    </td>
                  </tr>
                )
              })}
          </tbody>
        </Table>
        </div>
        <div>{pagination}</div>
        <EditDeploymentModal
          show={editInstanceModalOpen}
          toggle={setEditInstanceModalOpen}
          deployment={currentState.deployment}
        />
        <RemoveConfirmModal
          modalTitle="Remove Context"
          message={removeContextMessage}
          endpoint={removeContextURL}
          show={removeContextModalOpen}
          toggle={toggleRemoveModal}
          currentState={currentState}
          dispatch={dispatch}
        />
      </div>
    </>
  )
}
